/**
 * Traduce un error HTTP del backend a un texto legible para el panel.
 *
 * El backend ya responde en castellano (`message`, que puede ser string o, en errores de
 * validación, un array), así que ese texto es lo primero que se muestra. Lo que nunca debe
 * llegar a la pantalla es lo crudo: "Http failure response for …", una página HTML de un
 * proxy, o los textos por defecto de Nest en inglés ("Forbidden resource", "Cannot POST …").
 * En esos casos se arma el mensaje con `fallback` (qué falló) + la causa según el status
 * (por qué y qué hacer).
 */

/** Textos por defecto de Nest/Express que no le sirven a quien usa el panel. */
const RAW_DEFAULTS =
  /^(forbidden resource|forbidden|unauthorized|not found|bad request|conflict|internal server error|too many requests|service unavailable|bad gateway|gateway timeout|http failure|cannot (get|post|put|patch|delete) )/i;

function isUsable(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.startsWith('<')) return false; // página HTML de un proxy/balanceador
  return !RAW_DEFAULTS.test(t);
}

function withPeriod(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return /[.!?…:]$/.test(t) ? t : `${t}.`;
}

/** Una frase de una lista (errores de validación): mayúscula inicial y punto final. */
function sentence(text: string): string {
  const t = withPeriod(text);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Extrae el texto humano del cuerpo de error del backend, o null si no hay uno usable. */
function bodyMessage(body: unknown): string | null {
  if (body == null) return null;
  if (typeof body === 'string') {
    // Algunas respuestas llegan como texto (p. ej. un JSON sin parsear).
    try {
      return bodyMessage(JSON.parse(body));
    } catch {
      return isUsable(body) ? body.trim() : null;
    }
  }
  if (typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  const message = o['message'];
  if (Array.isArray(message)) {
    const parts = message.filter((m): m is string => typeof m === 'string' && isUsable(m));
    if (parts.length) return parts.map(sentence).join(' ');
  } else if (typeof message === 'string' && isUsable(message)) {
    return message.trim();
  }
  const errors = o['errors'];
  if (Array.isArray(errors)) {
    const parts = errors
      .map((e) => (e && typeof e === 'object' ? (e as Record<string, unknown>)['detail'] : null))
      .filter((d): d is string => typeof d === 'string' && isUsable(d));
    if (parts.length) return parts.map(sentence).join(' ');
  }
  return null;
}

function causeByStatus(status: number | undefined): string {
  if (status === 0) {
    return 'No hay conexión con el servidor: revisá tu conexión a internet o probá de nuevo en unos minutos.';
  }
  switch (status) {
    case 400:
    case 422:
      return 'Los datos enviados no son válidos: revisá los campos y probá de nuevo.';
    case 401:
      return 'Tu sesión expiró o ya no es válida: volvé a iniciar sesión.';
    case 403:
      return 'Tu rol no tiene permiso para esta acción. Si lo necesitás, pedíselo a un superadmin.';
    case 404:
      return 'No se encontró el recurso: puede que se haya eliminado. Recargá la pantalla.';
    case 409:
      return 'La operación choca con el estado actual del recurso: recargá la pantalla y revisá los datos.';
    case 413:
      return 'El contenido enviado es demasiado grande.';
    case 429:
      return 'Se hicieron demasiados pedidos seguidos: esperá unos segundos y probá de nuevo.';
    case 502:
    case 503:
    case 504:
      return 'El servidor o un servicio externo no está respondiendo: probá de nuevo en unos minutos.';
  }
  if (status != null && status >= 500) {
    return 'El servidor tuvo un problema inesperado: probá de nuevo en unos minutos y, si sigue pasando, avisale al equipo técnico.';
  }
  return 'Probá de nuevo en unos segundos.';
}

/**
 * @param err      el error recibido en `subscribe({ error })` (normalmente un HttpErrorResponse).
 * @param fallback qué falló, desde el punto de vista de quien usa el panel
 *                 (p. ej. "No se pudo eliminar la aplicación."). Se usa cuando el backend
 *                 no mandó un texto propio.
 * @param causes   causa a usar para un status puntual cuando la genérica no aplica
 *                 (p. ej. en el login un 401 son credenciales incorrectas, no una sesión vencida).
 */
export function apiErrorMessage(
  err: unknown,
  fallback: string,
  causes: Partial<Record<number, string>> = {},
): string {
  const e = err as { status?: number; error?: unknown } | null;
  const status = typeof e?.status === 'number' ? e.status : undefined;
  const body = e?.error;

  const fromBackend = status === 0 ? null : bodyMessage(body);
  const cause = (status != null && causes[status]) || causeByStatus(status);
  let text = fromBackend ?? `${withPeriod(fallback)} ${cause}`;

  // En un 5xx, el requestId es lo que permite encontrar el error en los logs del servidor
  // (el propio mensaje del backend pide informarlo), así que se muestra.
  const requestId =
    body && typeof body === 'object' ? (body as Record<string, unknown>)['requestId'] : null;
  if (status != null && status >= 500 && typeof requestId === 'string' && requestId) {
    text = `${withPeriod(text)} Código de seguimiento (requestId): ${requestId}.`;
  }
  return text;
}
